/**
 * Global image override layer.
 *
 * Every built-in (code-hardcoded) image on the site is resolved through this
 * module so it can be replaced or hidden from /admin without touching code.
 * Overrides are keyed by the original image path exactly as it appears in the
 * source (e.g. "/atlantis-v2/bay-sunrise.webp").
 *
 * No-skeleton behaviour: until the override map arrives, the built-in image
 * renders immediately; the swap happens when CMS data resolves.
 */

export interface ResolvedImage {
  url: string;
  alt?: string;
  hidden: boolean;
  overridden: boolean;
}

export interface ImageOverrideRecord {
  originalPath: string;
  url?: string | null;
  alt?: string | null;
  hidden?: boolean | null;
}

let overrides: Record<string, ImageOverrideRecord> = {};
let version = 0;
const listeners = new Set<() => void>();

/** Normalise a src so bundled assets and public paths share one key space. */
export function normaliseImageKey(src: string): string {
  if (!src) return src;
  if (/^https?:/.test(src)) return src;
  // Vite rewrites bundled assets to /assets/name-hash.ext — key those by filename.
  const bundled = src.match(/^\/assets\/(.+?)-[A-Za-z0-9_]{6,}(\.[a-z0-9]+)$/i);
  if (bundled) return `bundled:${bundled[1]}${bundled[2]}`;
  return src;
}

export function setImageOverrides(records: ImageOverrideRecord[]) {
  const next: Record<string, ImageOverrideRecord> = {};
  for (const r of records) {
    if (!r?.originalPath) continue;
    next[normaliseImageKey(r.originalPath)] = r;
    // Also index bundled-asset overrides by filename so hashed/dev URLs match.
    const file = r.originalPath.slice(r.originalPath.lastIndexOf('/') + 1);
    if (r.originalPath.startsWith('src/') || r.originalPath.includes('/src/')) {
      next[`bundled:${file}`] = r;
      next[`/${r.originalPath.replace(/^\/+/, '')}`] = r;
    }
  }
  overrides = next;
  version += 1;
  listeners.forEach((l) => l());
}

export function subscribeImageOverrides(listener: () => void) {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

export function getImageOverridesVersion() {
  return version;
}

export function getImageOverrideMap() {
  return overrides;
}

/**
 * Look up an override for a runtime src, tolerating the different shapes the
 * same asset can take: public path, dev-server source path (/src/assets/...)
 * and hashed production bundle path (/assets/name-HASH.ext, where the hash can
 * itself contain dashes).
 */
function lookupOverride(src: string): ImageOverrideRecord | undefined {
  const direct = overrides[normaliseImageKey(src)];
  if (direct) return direct;

  // Dev server: "/src/assets/..." vs registry key "src/assets/..."
  if (src.startsWith('/src/')) {
    const r = overrides[src.slice(1)];
    if (r) return r;
  }

  // Production bundle: peel trailing "-segment" chunks off the filename.
  const hashed = src.match(/^\/assets\/(.+)(\.[a-z0-9]+)$/i);
  if (hashed) {
    let base = hashed[1];
    while (base.includes('-')) {
      const r = overrides[`bundled:${base}${hashed[2]}`];
      if (r) return r;
      base = base.slice(0, base.lastIndexOf('-'));
    }
    const r = overrides[`bundled:${base}${hashed[2]}`];
    if (r) return r;
  }

  // Last resort: match by bare filename.
  return overrides[`bundled:${src.slice(src.lastIndexOf('/') + 1)}`];
}

export function resolveImage(src?: string | null, fallbackAlt?: string): ResolvedImage {
  const url = src || '';
  if (!url) return { url: '', alt: fallbackAlt, hidden: false, overridden: false };
  const record = lookupOverride(url);
  if (!record) return { url, alt: fallbackAlt, hidden: false, overridden: false };
  if (record.hidden) return { url, alt: fallbackAlt, hidden: true, overridden: true };
  return {
    url: record.url || url,
    alt: record.alt || fallbackAlt,
    hidden: false,
    overridden: !!record.url,
  };
}

/** Convenience for background-image / poster usages. */
export function resolveImageUrl(src?: string | null): string {
  const r = resolveImage(src);
  return r.hidden ? '' : r.url;
}
